import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Database, Users, Globe, Mail, CreditCard, Sparkles, AlertCircle } from 'lucide-react';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const lastUpdated = "January 24, 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Privacy Policy</span>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-teal-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1rem' }}>
            <Shield size={32} className="text-teal-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Panchi – AI Travel Co-Planner
          </p>
          <p className="text-sm text-gray-400 mt-4">Last updated: {lastUpdated}</p>
        </motion.div>
        
        {/* Content Sections */}
        <div className="space-y-8">
          {/* 1. Introduction */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-3">
              Welcome to Panchi ("we", "our", "us").
            </p>
            <p className="text-gray-600 mb-3">
              Panchi is an AI-powered travel planning platform that helps users create personalised trip plans.
            </p>
            <p className="text-gray-600 mb-3">
              Your privacy matters to us. This Privacy Policy explains:
            </p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                What data we collect
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                How we use it
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                How we protect it
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your choices and rights
              </li>
            </ul>
            <p className="text-gray-600 mt-3 font-medium">
              By using Panchi, you agree to this Privacy Policy.
            </p>
          </motion.section>

          {/* 2. Information We Collect */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Database size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">2. Information We Collect</h2>
            </div>
            
            <h3 className="font-semibold text-gray-800 mt-4 mb-2">a) Information you provide directly</h3>
            <p className="text-gray-600 mb-2">When you use Panchi, we may collect:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Name
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Email address
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Travel preferences (e.g. interests, pace, budget style)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Feedback or messages you send us
              </li>
            </ul>

            <h3 className="font-semibold text-gray-800 mt-6 mb-2">b) Login information</h3>
            <p className="text-gray-600 mb-2">If you sign in using Google, we only access:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your name
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your email address
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your profile picture (optional)
              </li>
            </ul>
            
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200" style={{ borderRadius: '0.75rem' }}>
              <div className="flex items-start gap-2">
                <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">We do NOT access:</p>
                  <ul className="text-amber-700 text-sm mt-1 space-y-0.5">
                    <li>• Gmail content</li>
                    <li>• Google Maps history</li>
                    <li>• Calendar data</li>
                    <li>• Contacts</li>
                    <li>• Any private Google data</li>
                  </ul>
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-gray-800 mt-6 mb-2">c) Usage & behavioural data</h3>
            <p className="text-gray-600 mb-2">To improve your experience, we collect:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Destinations searched
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Trips created or edited
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Preferences inferred from in-app actions
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Feature usage (e.g. events viewed, nudges clicked)
              </li>
            </ul>
            <p className="text-gray-500 text-sm mt-2 italic">
              This helps us personalise recommendations and improve the product.
            </p>

            <h3 className="font-semibold text-gray-800 mt-6 mb-2">d) Technical data</h3>
            <p className="text-gray-600 mb-2">We may collect:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Device type
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Browser or app version
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                IP address (for security and analytics)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Error logs
              </li>
            </ul>
          </motion.section>

          {/* 3. How We Use Your Information */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Lock size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">3. How We Use Your Information</h2>
            </div>
            <p className="text-gray-600 mb-3">We use your data to:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Create and manage your account
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Generate personalised travel plans
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Recommend events, transport, and stays
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Improve AI intelligence and product quality
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Respond to support and feedback
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Ensure security and prevent misuse
              </li>
            </ul>
            <p className="text-teal-600 font-semibold mt-4">
              We do not sell your personal data.
            </p>
          </motion.section>

          {/* 4. Personalisation & AI */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Sparkles size={20} className="text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">4. Personalisation & AI</h2>
            </div>
            <p className="text-gray-600 mb-3">Panchi uses AI to personalise suggestions based on:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your preferences
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Your in-app behaviour
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Aggregated, anonymous trends
              </li>
            </ul>
            <p className="text-gray-600 mt-3">Personalisation is designed to be helpful, not intrusive.</p>
            <p className="text-gray-600 mt-2">You can:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Opt out of personalisation
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Reset preferences anytime
              </li>
            </ul>
          </motion.section>

          {/* 5. Social & Crowd Insights */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Users size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">5. Social & Crowd Insights</h2>
            </div>
            <p className="text-gray-600 mb-3">Panchi may show aggregated insights, such as:</p>
            <ul className="space-y-1 text-gray-600 ml-4 italic">
              <li>"Many travellers are attending this event"</li>
              <li>"This destination is trending"</li>
            </ul>
            <p className="text-gray-600 mt-3">These insights:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Are anonymous
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Do not identify any individual
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Do not expose personal details
              </li>
            </ul>
          </motion.section>

          {/* 6. Payments & Support */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <CreditCard size={20} className="text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">6. Payments & Support</h2>
            </div>
            <p className="text-gray-600 mb-3">
              During beta, Panchi may offer voluntary support options (e.g. via PayPal).
            </p>
            <p className="text-gray-600">We:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Do not store payment details
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Do not process payments ourselves
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Redirect you to trusted third-party platforms
              </li>
            </ul>
          </motion.section>

          {/* 7. Sharing of Information */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Globe size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">7. Sharing of Information</h2>
            </div>
            <p className="text-gray-600 mb-3">We may share limited data with:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Service providers (hosting, analytics, APIs)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Only as required to run Panchi
              </li>
            </ul>
            <p className="text-teal-600 font-semibold mt-3">
              We never share personal data for advertising without consent.
            </p>
          </motion.section>

          {/* 8. Data Security */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Shield size={20} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">8. Data Security</h2>
            </div>
            <p className="text-gray-600 mb-3">We take reasonable measures to protect your data, including:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Secure servers
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Restricted access
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Encrypted communication
              </li>
            </ul>
            <p className="text-gray-500 text-sm mt-3 italic">
              However, no system is 100% secure.
            </p>
          </motion.section>

          {/* 9. Your Rights */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <Eye size={20} className="text-teal-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">9. Your Rights</h2>
            </div>
            <p className="text-gray-600 mb-3">You have the right to:</p>
            <ul className="space-y-1 text-gray-600 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Access your data
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Correct your data
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Delete your account
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-teal-500 mt-2 flex-shrink-0" style={{ borderRadius: '50%' }} />
                Opt out of personalisation
              </li>
            </ul>
            <p className="text-gray-600 mt-4">To exercise your rights, contact us at:</p>
            <a href="mailto:admin@panchi.world" className="flex items-center gap-2 text-teal-600 font-medium mt-2 hover:text-teal-500">
              <Mail size={16} />
              admin@panchi.world
            </a>
          </motion.section>

          {/* 10. Children's Privacy */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-600">
              Panchi is not intended for children under 13. We do not knowingly collect data from children.
            </p>
          </motion.section>

          {/* 11. Changes to This Policy */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600">
              We may update this Privacy Policy. Any changes will be reflected on this page with a new date.
            </p>
          </motion.section>

          {/* 12. Contact Us */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="p-6 bg-teal-50 border border-teal-100"
            style={{ borderRadius: '1.25rem' }}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-600 mb-4">If you have questions about privacy:</p>
            <a 
              href="mailto:admin@panchi.world"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold hover:bg-teal-700 transition-colors"
              style={{ borderRadius: '9999px' }}
            >
              <Mail size={18} />
              admin@panchi.world
            </a>
          </motion.section>
        </div>
        
        {/* Links */}
        <div className="mt-8 text-center">
          <Link to="/terms" className="text-teal-600 font-medium hover:text-teal-500">
            View Terms of Service →
          </Link>
        </div>
      </main>
    </div>
  );
}
